import axios, { AxiosInstance, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { ApiErrorResponse, UnprocessableEntityErrorResponse } from './model';
import { toast } from 'react-toastify';
import { BASE_URL } from 'api/config';
import { refreshSession, SessionExpiredError } from './auth/session';
import { getAccessToken } from './auth/tokenStore';

type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

const attachAuth = (client: AxiosInstance): AxiosInstance => {
  client.interceptors.request.use((config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
      config.headers.delete('Authorization');
    }
    return config;
  });

  client.interceptors.response.use(undefined, async (error: unknown) => {
    if (!isAxiosError(error) || error.response?.status !== 401) throw error;

    const config = error.config as RetriableConfig | undefined;
    if (!config || config._retriedAfterRefresh) throw error;

    config._retriedAfterRefresh = true;
    await refreshSession();

    return client.request(config);
  });

  return client;
};

const instance = attachAuth(
  axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
);

const multipartInstance = attachAuth(
  axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
);

const csvDownloadInstance = attachAuth(
  axios.create({
    baseURL: BASE_URL,
    responseType: 'blob',
    headers: {
      Accept: 'text/csv',
    },
  }),
);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (isAxiosError<UnprocessableEntityErrorResponse>(error) && error.response?.status === 422) {
    return error.response.data.detail?.[0]?.msg;
  }
  if (isAxiosError<ApiErrorResponse>(error)) {
    const detail = error.response?.data.detail;
    return typeof detail === 'string' ? detail : undefined;
  }
  console.error('Unexpected error:', error);
  return undefined;
};

const apiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const errorDetail = getApiErrorMessage(error);
  return !errorDetail || errorDetail === 'Internal server error' ? fallbackMessage : errorDetail;
};

const isSessionError = (error: unknown) =>
  error instanceof SessionExpiredError || (isAxiosError(error) && error.response?.status === 401);

const errorHandler = (fallbackMessage: string) => (error: Error) => {
  if (isSessionError(error)) return;

  const errorDetail = getApiErrorMessage(error);
  if (!errorDetail || errorDetail === 'Internal server error') {
    toast.error(fallbackMessage);
    return;
  }

  toast.error(`Error: ${errorDetail}`);
};

export { BASE_URL, multipartInstance, csvDownloadInstance, errorHandler, apiErrorMessage };
export default instance;
